import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Loader2, Mail } from "lucide-react";
import { isAuthRetryableFetchError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/recuperar-senha")({
  head: () => ({
    meta: [
      { title: "Recuperar Senha — Loja EG Elétrica" },
      { name: "description", content: "Recupere o acesso à sua conta." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: RecuperarSenhaPage,
});

function RecuperarSenhaPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [transportError, setTransportError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setTransportError(false);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/redefinir-senha`,
    });
    setSubmitting(false);
    if (error) {
      console.error("[recuperar-senha]", error.message);
      // Falha de transporte/config (ex.: credenciais do Supabase ausentes no
      // navegador) significa que o pedido nem chegou a ser processado — isso
      // é um problema técnico nosso, não revela nada sobre a conta do
      // usuário, então mostramos um aviso de verdade em vez da mensagem
      // neutra de sempre.
      if (isAuthRetryableFetchError(error)) {
        setTransportError(true);
        return;
      }
    }
    // Para qualquer resposta normal do Supabase (exista ou não o e-mail),
    // mostramos sempre a mesma mensagem, para não revelar quais e-mails têm
    // conta na loja.
    setSent(true);
  };

  if (transportError) {
    return (
      <div className="mx-auto max-w-sm px-4 py-12 text-center">
        <h1 className="font-display text-2xl font-bold text-eg-dark mb-3">
          Não foi possível enviar
        </h1>
        <p className="mb-4 text-sm text-muted-foreground">
          Tivemos um problema técnico e não conseguimos processar seu pedido agora. Tente novamente
          em alguns minutos ou fale com a gente pelo WhatsApp.
        </p>
        <button
          type="button"
          onClick={() => setTransportError(false)}
          className="text-sm font-medium text-eg-goldtext hover:underline"
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="mx-auto max-w-sm px-4 py-12 text-center">
        <h1 className="font-display text-2xl font-bold text-eg-dark mb-3">Verifique seu e-mail</h1>
        <p className="text-sm text-muted-foreground">
          Se houver uma conta cadastrada com <strong>{email}</strong>, você receberá um link para
          redefinir sua senha.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-sm px-4 py-12">
      <h1 className="font-display text-2xl font-bold text-eg-dark mb-6">Recuperar Senha</h1>
      <form
        onSubmit={handleSubmit}
        className="space-y-4 rounded-lg bg-white border border-border p-6"
      >
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-eg-dark">E-mail</span>
          <input
            type="email"
            required
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <button
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-eg-gold px-4 py-2.5 font-semibold text-eg-dark hover:bg-eg-goldhover disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />}
          Enviar link de recuperação
        </button>
        <div className="text-center text-xs text-muted-foreground">
          <Link to="/login" className="hover:text-eg-gold transition-colors">
            Voltar para o login
          </Link>
        </div>
      </form>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.375rem;
          border: 1px solid var(--border);
          background: white;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .input:focus {
          border-color: var(--eg-gold);
          box-shadow: 0 0 0 2px color-mix(in oklab, var(--eg-gold) 30%, transparent);
        }
      `}</style>
    </div>
  );
}
